import React, { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "react-query";

import { getErrorMessage } from "services/api";
import { getPromotions, deletePromotion } from "features/Promotions/api";
import { PromotionModal } from "features/Promotions/PromotionModal";

import { PageLayout, ActionsRow } from "components/PageLayout";
import { BoldText } from "components/Text";
import { Button } from "components/Button";
import { Table } from "components/Table";
import { SearchInput } from "components/SearchInput";
import { useModal, ConfirmationModal, InfoModal } from "components/Modal";

import styles from "./styles.module.scss";

const DAY_LABELS = [
  ["monday", "Пн"],
  ["tuesday", "Вт"],
  ["wednesday", "Ср"],
  ["thursday", "Чт"],
  ["friday", "Пт"],
  ["saturday", "Сб"],
  ["sunday", "Вс"],
];

const daysCell = (params) => {
  const row = params.data ?? {};
  const active = DAY_LABELS.filter(([field]) => Number(row[field]) === 1);
  if (!active.length) return "";
  if (active.length === DAY_LABELS.length) return "Все дни";
  return active.map(([, label]) => label).join(", ");
};

const typeCell = (params) =>
  params.data?.ispercent == null
    ? ""
    : Number(params.data.ispercent) === 1
      ? "Процентная"
      : "Ценовая";

const itemsCountCell = (params) => params.data?.items?.length ?? 0;

export const PromotionsPage = () => {
  const queryClient = useQueryClient();
  const { openModal, closeModal } = useModal();

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const promotionsQuery = useQuery(["promotions"], getPromotions);
  const promotions = useMemo(
    () => promotionsQuery.data?.items ?? [],
    [promotionsQuery.data]
  );

  const filtered = useMemo(() => {
    if (!search) return promotions;
    const lower = search.toLowerCase();
    return promotions.filter((p) =>
      [p.name, p.importcode, p.stockid]
        .map((v) => String(v ?? "").toLowerCase())
        .some((v) => v.includes(lower))
    );
  }, [promotions, search]);

  const colDefs = useMemo(
    () => [
      { field: "stockid", headerName: "Код", width: 90 },
      { field: "name", headerName: "Наименование", flex: 2, minWidth: 220 },
      {
        field: "ispercent",
        headerName: "Тип",
        width: 120,
        cellRenderer: typeCell,
      },
      { field: "starttime", headerName: "Действует с", width: 120 },
      { field: "endtime", headerName: "Действует по", width: 125 },
      {
        colId: "days",
        headerName: "Дни",
        flex: 1,
        minWidth: 160,
        sortable: false,
        cellRenderer: daysCell,
      },
      {
        colId: "itemsCount",
        headerName: "Товаров",
        width: 100,
        sortable: false,
        cellRenderer: itemsCountCell,
      },
    ],
    []
  );

  const openPromotionModal = (promotion) =>
    openModal(<PromotionModal promotion={promotion} />);

  const onDeleteClick = () => {
    if (!selected) return;
    openModal(
      <ConfirmationModal
        title={`Вы действительно хотите удалить акцию "${selected.name}"?`}
        onConfirm={async () => {
          try {
            await deletePromotion(selected.stockid);
            setSelected(null);
            queryClient.invalidateQueries(["promotions"]);
            closeModal();
          } catch (e) {
            openModal(
              <InfoModal title={getErrorMessage(e)} onClose={closeModal} />
            );
          }
        }}
        onClose={closeModal}
      />
    );
  };

  return (
    <div className={styles.page}>
      <PageLayout
        header={<BoldText>Акции</BoldText>}
        footer={
          <ActionsRow>
            <Button icon="plus" onClick={() => openPromotionModal(null)}>
              Добавить акцию
            </Button>
            <Button
              icon="delete"
              onClick={onDeleteClick}
              disabled={!selected}
              title="Удалить акцию"
            />
            <Button
              icon="info"
              onClick={() => openPromotionModal(selected)}
              disabled={!selected}
              title="Свойства акции"
            />
          </ActionsRow>
        }
        bodyFill
      >
        <div className={styles.searchRow}>
          <SearchInput
            value={search}
            onChange={setSearch}
            placeholder="Поиск"
          />
        </div>
        <Table
          fill
          rowData={filtered}
          colDefs={colDefs}
          rowKey="stockid"
          selectedId={selected?.stockid}
          onSelect={(row) =>
            setSelected(
              promotions.find((p) => p.stockid === row.stockid) || null
            )
          }
          onOpen={(row) => {
            const promotion = promotions.find(
              (p) => p.stockid === row.stockid
            );
            if (promotion) {
              setSelected(promotion);
              openPromotionModal(promotion);
            }
          }}
          loading={promotionsQuery.isLoading}
          error={
            promotionsQuery.isError
              ? getErrorMessage(promotionsQuery.error)
              : ""
          }
          onRetry={promotionsQuery.refetch}
        />
      </PageLayout>
    </div>
  );
};
